import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { AssetManagement } from '@aptsi-types';

import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { SuccessDialogComponent } from 'src/app/shared/dialogs/success-dialog/success-dialog.component';
import { AssetsViewService } from '../../shared/services/assets-view.service';

export interface DataAssetManualUpdateDialogData {
  assetId: string;
}

@Component({
  selector: 'app-data-asset-manual-update',
  templateUrl: './data-asset-manual-update.component.html',
  styleUrls: ['./data-asset-manual-update.component.scss'],
})
export class DataAssetManualUpdateComponent implements OnInit {
  form!: FormGroup;

  dataAssetId = '';
  organizationId = '';

  loading = false;
  saving = false;
  loadFailed = false;
  pageError = '';
  loadError = '';

  importedBy = '';
  importedByKeycloakId = '';
  private existingImportedAt = '';

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: DataAssetManualUpdateDialogData,
    private readonly dialogRef: MatDialogRef<DataAssetManualUpdateComponent>,
    private readonly fb: FormBuilder,
    private readonly assetsViewService: AssetsViewService,
    private readonly keycloakStateService: KeycloakStateService,
    private readonly keycloakService: KeycloakService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.dataAssetId = String(this.dialogData?.assetId ?? '').trim();

    this.importedBy = this.getImportedByFromKeycloak();
    this.importedByKeycloakId = this.getKeycloakUserId();
    this.organizationId = this.getOrganizationIdFromContext();

    this.buildForm();

    if (!this.dataAssetId) {
      this.loadFailed = true;
      this.loadError = 'Data Asset ID was not supplied. Unable to load this asset.';
      return;
    }

    this.loadManualDataAsset();
  }

  get controls(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  private buildForm(): void {
    // As in the existing create/update screen, only Name and Status are required.
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/.*\S.*/)]],
      description: [''],
      status: ['ACTIVE', [Validators.required, Validators.pattern(/.*\S.*/)]],
      assetTechnicalName: [''],
      qualifiedName: [''],
      creationTimestamp: [''],
      createdBy: [''],
      lastModifiedTimestamp: [''],
      lastModifiedBy: [''],
      entityVersion: [''],
      entityStatus: [''],
      collectionId: [''],
      classificationName: [''],
      upstreamAssetRelationships: [''],
      uniqueIdentifier: [''],
    });
  }

  loadManualDataAsset(): void {
    if (!this.dataAssetId || this.loading) {
      return;
    }

    this.loading = true;
    this.loadFailed = false;
    this.loadError = '';
    this.pageError = '';

    this.assetsViewService.getManualDataAsset(this.dataAssetId).subscribe({
      next: (response: AssetManagement) => {
        this.loading = false;

        if (!response) {
          this.loadFailed = true;
          this.loadError = 'No details were found for this data asset.';
          return;
        }

        const asset = response as AssetManagement & Record<string, unknown>;

        this.organizationId = this.asText(asset.organizationId).trim() || this.organizationId;
        this.existingImportedAt = this.asText(asset.importedAt).trim();
        this.importedBy = this.asText(asset.importedBy).trim() || this.importedBy;
        this.importedByKeycloakId =
          this.asText(asset.importedByKeycloakId).trim() || this.importedByKeycloakId;

        this.form.patchValue({
          name: this.asText(asset.name),
          description: this.asText(asset.description),
          status: this.asText(asset.status),
          assetTechnicalName: this.asText(asset.assetTechnicalName),
          qualifiedName: this.asText(asset.qualifiedName),
          creationTimestamp: this.asText(asset.creationTimestamp),
          createdBy: this.asText(asset.createdBy),
          lastModifiedTimestamp: this.asText(asset.lastModifiedTimestamp),
          lastModifiedBy: this.asText(asset.lastModifiedBy),
          entityVersion: this.asText(asset.entityVersion),
          entityStatus: this.asText(asset.entityStatus),
          collectionId: this.asText(asset.collectionId),
          classificationName: this.normalizeClassification(asset.classificationName),
          upstreamAssetRelationships: this.toFormText(asset.upstreamAssetRelationships),
          uniqueIdentifier: this.asText(asset.uniqueIdentifier),
        });
      },
      error: error => {
        this.loading = false;
        this.loadFailed = true;
        this.loadError =
          this.extractMessage(error) || 'Unable to load the data asset details.';
      },
    });
  }

  save(): void {
    if (this.saving || this.loading || this.loadFailed) {
      return;
    }

    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.pageError = 'Enter a name and select or enter a status before saving.';
      return;
    }

    if (!this.organizationId) {
      this.pageError =
        'Organization ID is unavailable. Ensure it is present in the asset details or the signed-in user context.';
      return;
    }

    if (!this.importedByKeycloakId) {
      this.pageError = 'Unable to identify the signed-in user. Please sign in again and retry.';
      return;
    }

    this.pageError = '';
    this.saving = true;

    const value = this.form.getRawValue();
    const fields = {
      name: this.asText(value.name).trim(),
      description: this.asText(value.description).trim(),
      status: this.asText(value.status).trim(),
      assetTechnicalName: this.asText(value.assetTechnicalName).trim(),
      qualifiedName: this.asText(value.qualifiedName).trim(),
      creationTimestamp: this.asText(value.creationTimestamp).trim(),
      createdBy: this.asText(value.createdBy).trim(),
      lastModifiedTimestamp: this.asText(value.lastModifiedTimestamp).trim(),
      lastModifiedBy: this.asText(value.lastModifiedBy).trim(),
      entityVersion: this.asText(value.entityVersion).trim(),
      entityStatus: this.asText(value.entityStatus).trim(),
      collectionId: this.asText(value.collectionId).trim(),
      classificationName: this.asText(value.classificationName).trim(),
      upstreamAssetRelationships: this.asText(value.upstreamAssetRelationships).trim(),
      importedAt: this.existingImportedAt || new Date().toISOString(),
      uniqueIdentifier: this.asText(value.uniqueIdentifier).trim(),
    };

    this.assetsViewService.updateManualDataAsset(
      this.dataAssetId,
      this.organizationId,
      fields.name,
      fields.description,
      fields.status,
      fields.assetTechnicalName,
      fields.qualifiedName,
      fields.creationTimestamp,
      fields.createdBy,
      fields.lastModifiedTimestamp,
      fields.lastModifiedBy,
      fields.entityVersion,
      fields.entityStatus,
      fields.collectionId,
      fields.classificationName,
      fields.upstreamAssetRelationships,
      fields.importedAt,
      fields.uniqueIdentifier,
      this.importedBy,
      this.importedByKeycloakId,
    ).subscribe({
      next: response => this.onSaveSuccess(response),
      error: error => this.onSaveError(
        error,
        'Unable to update the data asset. Please review the details and try again.',
      ),
    });
  }

  private onSaveSuccess(response: AssetManagement): void {
    const responseMessage = this.extractMessage(response);
    if (responseMessage.toLowerCase().includes('already exists')) {
      this.saving = false;
      this.pageError = responseMessage;
      return;
    }

    this.saving = false;
    const message = responseMessage || 'Data asset updated successfully.';

    const successDialogRef = this.dialog.open(SuccessDialogComponent, {
      width: '380px',
      data: {
        isCreate: false,
        isCreateUpdate: true,
        title: 'Data Asset',
        message,
      },
    });

    successDialogRef.afterClosed().subscribe(() => {
      this.dialogRef.close({ updated: true, response });
    });
  }

  private onSaveError(error: unknown, fallbackMessage: string): void {
    this.saving = false;
    this.pageError = this.extractMessage(error) || fallbackMessage;
  }

  cancel(): void {
    if (!this.saving) {
      this.dialogRef.close({ updated: false });
    }
  }

  close(): void {
    this.cancel();
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return Boolean(control && control.invalid && (control.touched || control.dirty));
  }

  private getTokenClaims(): Record<string, unknown> {
    try {
      const keycloak = this.keycloakService.getKeycloakInstance();
      return (keycloak?.tokenParsed || {}) as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private getImportedByFromKeycloak(): string {
    const token = this.getTokenClaims();
    const userLabel = token['preferred_username'] || token['name'] || token['email'];
    return userLabel ? String(userLabel) : '';
  }

  private getKeycloakUserId(): string {
    try {
      const stateUserId = this.keycloakStateService.getUserId();
      if (stateUserId) {
        return String(stateUserId);
      }
    } catch {
      // Fall back to the Keycloak subject claim.
    }

    const subject = this.getTokenClaims()['sub'];
    return subject ? String(subject) : '';
  }

  private getOrganizationIdFromContext(): string {
    const claims = this.getTokenClaims();
    const candidate =
      claims['organizationId'] ??
      claims['organization_id'] ??
      claims['organizationID'] ??
      claims['orgId'] ??
      claims['org_id'];

    if (candidate !== null && candidate !== undefined && String(candidate).trim()) {
      return String(candidate).trim();
    }

    const organizationClaim = claims['organization'];
    if (typeof organizationClaim === 'string' && organizationClaim.trim()) {
      return organizationClaim.trim();
    }

    if (organizationClaim && typeof organizationClaim === 'object') {
      const organization = organizationClaim as Record<string, unknown>;
      const id = organization['organizationId'] ?? organization['id'] ?? organization['orgId'];
      if (id !== null && id !== undefined && String(id).trim()) {
        return String(id).trim();
      }
    }

    try {
      for (const key of ['organizationId', 'organization_id', 'orgId', 'org_id']) {
        const storedValue = localStorage.getItem(key);
        if (storedValue?.trim()) {
          return storedValue.trim();
        }
      }
    } catch {
      // Storage may be unavailable in this browser context.
    }

    return '';
  }

  private normalizeClassification(value: unknown): string {
    if (Array.isArray(value)) {
      return value.map(item => this.asText(item)).filter(Boolean).join(', ');
    }
    return this.asText(value);
  }

  private toFormText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }

  private asText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  private extractMessage(responseOrError: unknown): string {
    if (!responseOrError) {
      return '';
    }

    if (typeof responseOrError === 'string') {
      return responseOrError;
    }

    if (typeof responseOrError !== 'object') {
      return '';
    }

    const candidate = responseOrError as {
      message?: unknown;
      error?: { message?: unknown; errorMessage?: unknown; details?: unknown } | string;
      errorMessage?: unknown;
    };
    const nested = candidate.error;
    const candidates: unknown[] = [
      candidate.message,
      typeof nested === 'object' && nested !== null ? nested.message : nested,
      typeof nested === 'object' && nested !== null ? nested.errorMessage : undefined,
      typeof nested === 'object' && nested !== null ? nested.details : undefined,
      candidate.errorMessage,
    ];

    const message = candidates.find(item => typeof item === 'string' && item.trim());
    return message ? String(message).trim() : '';
  }
}
